export const SUPPORTED_LOCALES = ['en', 'ja'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const FALLBACK_LOCALE: SupportedLocale = 'en';
export const LNG_STORAGE_KEY = 'mb-lng';
export const isSupportedLocale = (v: string): v is SupportedLocale =>
	(SUPPORTED_LOCALES as readonly string[]).includes(v);

/**
 * Human label for each supported locale, shown in its own script
 * (English / 日本語). Single source of truth for language pickers — both the
 * global `LanguageMenu` and the workflow-builder `OutputLanguageSelect` read
 * from here. If a locale is added to `SUPPORTED_LOCALES` without a label, call
 * sites fall back to the raw code.
 */
export const LOCALE_LABELS: Record<string, string> = {
	en: 'English',
	ja: '日本語',
};

/**
 * Longer label for the account-level language switcher, glossing the endonym in
 * English (`日本語 (Japanese)`) so someone stranded in a language they cannot
 * read can still identify the row and switch back.
 *
 * Deliberately separate from `LOCALE_LABELS`: the compact form is what belongs
 * in space-constrained pickers such as the workflow-builder's
 * `OutputLanguageSelect`, and widening those was not the intent here.
 * Per the profile-menu design (Ravi, 2026-07-21).
 */
export const LOCALE_MENU_LABELS: Record<string, string> = {
	en: 'English',
	ja: '日本語 (Japanese)',
};

/**
 * Canonicalize a BCP-47 tag to its base language, e.g. `ja-JP` → `ja`,
 * `en_US` → `en`. Japanese browsers report `navigator.language` as `ja-JP`,
 * but the catalog is keyed by the base language, so we must normalize before
 * fetching/resolving or those users miss the `ja` rows and fall back to
 * English despite Japanese translations existing.
 */
export const normalizeLocale = (locale: string): string =>
	(locale.split(/[-_]/)[0] ?? locale).toLowerCase();
